import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Platform,
  ActivityIndicator,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../../../Admin/constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import ObsImageUploadModal, { KfaImage } from './ObsImageUploadModal';

// ─── Colour tokens (mapped from HTML CSS vars) ────────────────────────────────
// before  → nav-blue-700 / nav-blue-900
// during  → nav-green-700 / nav-green-text (≈ green600)
// after   → nav-blue-900 / nav-blue-700
const STAGE_COLORS = {
  before:  { bg: AdminColors.blue700,  border: AdminColors.blue900,  subColor: AdminColors.blue75 },
  during:  { bg: '#276027',            border: '#0C420B',            subColor: '#CAF3C6'          }, // nav-green-700 / green-text / green-75
  after:   { bg: AdminColors.blue900,  border: AdminColors.blue700,  subColor: AdminColors.blue75 },
} as const;

// Band colours for During sub-sections
const BAND_COLORS = {
  pit:     { bg: AdminColors.blue50,      text: AdminColors.blue900   },
  ergo:    { bg: AdminColors.green75,     text: '#0C420B'             }, // nav-green-75 / nav-green-text
  process: { bg: '#FCECDE',              text: '#4D2E0C'             }, // nav-orange-75 / nav-orange-text
} as const;

// ─── CheckRow ─────────────────────────────────────────────────────────────────
interface CheckRowProps {
  label: string;
  checked: boolean;
  photoCount: number;
  onToggle: (v: boolean) => void;
  onCameraPress: () => void;
  /** Completed-activity view: checkbox is locked and the camera only opens when images exist. */
  isReadOnly?: boolean;
}

const CheckRow: React.FC<CheckRowProps> = ({
  label, checked, photoCount, onToggle, onCameraPress, isReadOnly,
}) => (
  <View style={[styles.checkRow, checked && styles.checkRowChecked]}>
    <TouchableOpacity
      style={styles.checkboxWrap}
      onPress={() => onToggle(!checked)}
      disabled={isReadOnly}
      accessibilityRole="checkbox"
      accessibilityState={{ checked, disabled: isReadOnly }}
    >
      <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
        {checked && <MaterialIcons name="check" size={13} color="#fff" />}
      </View>
      <Text style={styles.checkLabel}>{label}</Text>
    </TouchableOpacity>

    <TouchableOpacity
      style={[styles.cameraBtn, photoCount > 0 && styles.cameraBtnActive]}
      onPress={onCameraPress}
      disabled={isReadOnly && photoCount === 0}
      accessibilityLabel={isReadOnly ? `View images for ${label}` : `Add images for ${label}`}
    >
      <MaterialIcons name="photo-camera" size={18} color={AdminColors.blue700} />
      {photoCount > 0 && (
        <View style={styles.cameraBadge}>
          <Text style={styles.cameraBadgeText}>{photoCount}</Text>
        </View>
      )}
    </TouchableOpacity>
  </View>
);

// ─── CheckList (grid + add row) ───────────────────────────────────────────────
interface CheckListProps {
  category: string;
  questions: string[];
  checkedItems: Set<string>;
  addInput: string;
  onToggle: (text: string, checked: boolean) => void;
  onAddInputChange: (val: string) => void;
  onAddItem: () => void;
  /** Returns the number of images saved for a given question text */
  getPhotoCount: (q: string) => number;
  /** Returns saved images for a given question text */
  getPhotos: (q: string) => KfaImage[];
  /** Called with updated images when user presses Proceed in the upload modal */
  onSavePhotos: (q: string, images: KfaImage[]) => void;
  /** Shows a loading indicator below the checklist while items are being fetched */
  isLoading?: boolean;
  /** Completed-activity view — locks every input (see CheckRow). */
  isReadOnly?: boolean;
}

const CheckList: React.FC<CheckListProps> = ({
  category, questions, checkedItems, addInput,
  onToggle, onAddInputChange, onAddItem,
  getPhotoCount, getPhotos, onSavePhotos, isLoading, isReadOnly,
}) => {
  const [activeKfa, setActiveKfa] = useState<string | null>(null);

  return (
    <View style={styles.checkList}>
      {/* Note if no questions for role/type */}
      {!isLoading && questions.length === 0 && (
        <Text style={styles.emptyText}>No questions for this role and observation type.</Text>
      )}

      {/* Grid of check rows */}
      {questions.length > 0 && (
        <View style={styles.checkGrid}>
          {questions.map((q) => (
            <CheckRow
              key={q}
              label={q}
              checked={checkedItems.has(q)}
              photoCount={getPhotoCount(q)}
              onToggle={(v) => onToggle(q, v)}
              onCameraPress={() => setActiveKfa(q)}
              isReadOnly={isReadOnly}
            />
          ))}
        </View>
      )}

      {/* Loading indicator — shown below the checklist while items are being fetched */}
      {isLoading && (
        <View style={styles.checklistLoadingRow}>
          <ActivityIndicator size="small" color={AdminColors.blue700} />
          <Text style={styles.checklistLoadingText}>Loading checklist items…</Text>
        </View>
      )}

      {/* Add custom item row */}
      {!isReadOnly && (
        <View style={styles.addRow}>
          <TextInput
            style={styles.addInput}
            value={addInput}
            onChangeText={onAddInputChange}
            placeholder="Add an additional item"
            placeholderTextColor={AdminColors.textMuted}
            accessibilityLabel={`Additional ${category} checklist item`}
            returnKeyType="done"
            onSubmitEditing={onAddItem}
          />
          <TouchableOpacity style={styles.addBtn} onPress={onAddItem}>
            <Text style={styles.addBtnText}>Add item</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Image upload modal — renders when a camera icon is tapped */}
      {activeKfa !== null && (
        <ObsImageUploadModal
          visible
          readOnly={isReadOnly}
          kfaLabel={activeKfa}
          savedImages={getPhotos(activeKfa)}
          onClose={() => setActiveKfa(null)}
          onSave={(images) => { onSavePhotos(activeKfa, images); setActiveKfa(null); }}
        />
      )}
    </View>
  );
};

// ─── BeforeAfterCard ─────────────────────────────────────────────────────────
interface StageCardProps {
  stageKey: 'before' | 'after';
  stepNumber: number;
  title: string;
  subtitle: string;
  category: string;
  questions: string[];
  checkedItems: Set<string>;
  addInput: string;
  onToggle: (text: string, checked: boolean) => void;
  onAddInputChange: (val: string) => void;
  onAddItem: () => void;
  getPhotoCount: (q: string) => number;
  getPhotos: (q: string) => KfaImage[];
  onSavePhotos: (q: string, images: KfaImage[]) => void;
  isLoading?: boolean;
  isReadOnly?: boolean;
}

export const StageCard: React.FC<StageCardProps> = ({
  stageKey, stepNumber, title, subtitle,
  category, questions, checkedItems, addInput,
  onToggle, onAddInputChange, onAddItem,
  getPhotoCount, getPhotos, onSavePhotos, isLoading, isReadOnly,
}) => {
  const colors = STAGE_COLORS[stageKey];
  return (
    <View style={styles.card}>
      {/* Coloured stage header */}
      <View style={[styles.stageHead, { backgroundColor: colors.bg, borderBottomColor: colors.border }]}>
        <Text style={styles.stageHeadTitle}>{stepNumber}. {title}</Text>
        <Text style={[styles.stageHeadSub, { color: colors.subColor }]}>{subtitle}</Text>
      </View>
      {/* Check list */}
      <View style={styles.stageBody}>
        <CheckList
          category={category}
          questions={questions}
          checkedItems={checkedItems}
          addInput={addInput}
          onToggle={onToggle}
          onAddInputChange={onAddInputChange}
          onAddItem={onAddItem}
          getPhotoCount={getPhotoCount}
          getPhotos={getPhotos}
          onSavePhotos={onSavePhotos}
          isLoading={isLoading}
          isReadOnly={isReadOnly}
        />
      </View>
    </View>
  );
};

// ─── DuringCard (3 collapsible accordion bands) ───────────────────────────────
interface DuringBand {
  key: 'pit' | 'ergo' | 'process';
  label: string;
  sub: string;
  questions: string[];
  checkedCount: number;
}

interface DuringCardProps {
  bands: DuringBand[];
  collapsedBands: Set<string>;
  checkedItems: Set<string>;
  addInputs: Record<string, string>;
  onToggleBand: (key: string) => void;
  onToggleItem: (text: string, checked: boolean) => void;
  onAddInputChange: (cat: string, val: string) => void;
  onAddItem: (cat: string) => void;
  getPhotoCount: (q: string) => number;
  getPhotos: (q: string) => KfaImage[];
  onSavePhotos: (q: string, images: KfaImage[]) => void;
  isLoading?: boolean;
  isReadOnly?: boolean;
}

export const DuringCard: React.FC<DuringCardProps> = ({
  bands, collapsedBands, checkedItems, addInputs,
  onToggleBand, onToggleItem, onAddInputChange, onAddItem,
  getPhotoCount, getPhotos, onSavePhotos, isLoading, isReadOnly,
}) => {
  const stageColors = STAGE_COLORS.during;
  return (
    <View style={styles.card}>
      {/* During stage header */}
      <View style={[styles.stageHead, { backgroundColor: stageColors.bg, borderBottomColor: stageColors.border }]}>
        <Text style={styles.stageHeadTitle}>2. During Activity</Text>
        <Text style={[styles.stageHeadSub, { color: stageColors.subColor }]}>All three categories are reviewed together</Text>
      </View>

      {/* Accordion queue */}
      <View style={styles.obsQueue}>
        {bands.map((band, idx) => {
          const isCollapsed = collapsedBands.has(band.key);
          const bandColors  = BAND_COLORS[band.key];
          return (
            <View key={band.key} style={[styles.obsBand, idx > 0 && styles.obsBandBorder]}>
              {/* Band header */}
              <TouchableOpacity
                style={[styles.obsBandHead, { backgroundColor: bandColors.bg }]}
                onPress={() => onToggleBand(band.key)}
                accessibilityRole="button"
                accessibilityState={{ expanded: !isCollapsed }}
              >
                <MaterialIcons
                  name={isCollapsed ? 'chevron-right' : 'expand-more'}
                  size={20}
                  color={bandColors.text}
                  style={styles.bandCaret}
                />
                <Text style={[styles.bandTitle, { color: bandColors.text }]}>{band.label}</Text>
                <Text style={[styles.bandCount, { color: bandColors.text }]}>
                  {band.checkedCount}/{band.questions.length}
                </Text>
                <Text style={[styles.bandSub, { color: bandColors.text }]}>{band.sub}</Text>
              </TouchableOpacity>

              {/* Band body (checklist) — hidden when collapsed */}
              {!isCollapsed && (
                <View style={styles.obsBandBody}>
                  <CheckList
                    category={band.key}
                    questions={band.questions}
                    checkedItems={checkedItems}
                    addInput={addInputs[band.key] ?? ''}
                    onToggle={onToggleItem}
                    onAddInputChange={(val) => onAddInputChange(band.key, val)}
                    onAddItem={() => onAddItem(band.key)}
                    getPhotoCount={getPhotoCount}
                    getPhotos={getPhotos}
                    onSavePhotos={onSavePhotos}
                    isLoading={isLoading}
                    isReadOnly={isReadOnly}
                  />
                </View>
              )}
            </View>
          );
        })}
      </View>
    </View>
  );
};

// ─── Instruction note bar ─────────────────────────────────────────────────────
export const ObsNoteBar: React.FC = () => (
  <View style={styles.noteBar}>
    <MaterialIcons name="info-outline" size={15} color={AdminColors.blue700} style={{ flexShrink: 0 }} />
    <Text style={styles.noteText}>
      Select any behaviors or observations you see below. Use the camera on a row to attach images.
    </Text>
  </View>
);

// ─── Styles ───────────────────────────────────────────────────────────────────
const isWeb = Platform.OS === 'web';

const styles = StyleSheet.create({
  // ── Outer card ────────────────────────────────────────────────────────
  card: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    overflow: 'hidden',
  },

  // ── Stage head ────────────────────────────────────────────────────────
  stageHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 48,
    paddingHorizontal: 18,
    paddingVertical: 13,
    borderBottomWidth: 4,
    gap: 12,
  },
  stageHeadTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 15,
    color: '#fff',
  },
  stageHeadSub: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
  },

  // ── Stage body ────────────────────────────────────────────────────────
  stageBody: {
    padding: 18,
  },

  // ── Check list + grid ─────────────────────────────────────────────────
  checkList: {
    gap: 8,
  },
  // CSS grid on web so every cell is the same width — flexbox + flex:1 stretches
  // whichever rows don't fill all columns (e.g. a lone last item).
  checkGrid: isWeb
    ? ({
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: 8,
      } as any)
    : {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
      },
  emptyText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textSecondary,
    padding: 8,
  },
  checklistLoadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
  },
  checklistLoadingText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textSecondary,
  },

  // ── Check row ─────────────────────────────────────────────────────────
  checkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    minHeight: 44,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: AdminColors.bgSurface,
    borderWidth: 1,
    borderColor: AdminColors.borderTable,
    borderRadius: 8,
    width: isWeb ? 'auto' : '100%',
  },
  checkRowChecked: {
    borderColor: AdminColors.blue700,
    backgroundColor: AdminColors.blue25,
    // inset left accent approximated with a left border shadow
    borderLeftWidth: 4,
    borderLeftColor: AdminColors.blue700,
  },
  checkboxWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    minWidth: 0,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 3,
    borderWidth: 1.5,
    borderColor: AdminColors.gray200,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 1,
  },
  checkboxChecked: {
    backgroundColor: AdminColors.blue700,
    borderColor: AdminColors.blue700,
  },
  checkLabel: {
    flex: 1,
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textPrimary,
    lineHeight: 18,
  },

  // ── Camera button ─────────────────────────────────────────────────────
  cameraBtn: {
    width: 44,
    height: 44,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.borderTable,
    backgroundColor: AdminColors.bgSurface,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    position: 'relative',
  },
  cameraBtnActive: {
    borderColor: AdminColors.blue700,
    backgroundColor: AdminColors.blue50,
  },
  cameraBadge: {
    position: 'absolute',
    top: 2,
    right: 2,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: AdminColors.blue700,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 3,
  },
  cameraBadgeText: {
    fontFamily: FontFamily.bold,
    fontSize: 9,
    color: '#fff',
  },

  // ── Add-item row ──────────────────────────────────────────────────────
  addRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 2,
  },
  addInput: {
    flex: 1,
    height: 40,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textPrimary,
    backgroundColor: AdminColors.bgSurface,
  },
  addBtn: {
    height: 40,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: AdminColors.bgSurface,
  },
  addBtnText: {
    fontFamily: FontFamily.medium,
    fontSize: 13,
    color: AdminColors.textPrimary,
  },

  // ── Obs-queue (During accordion) ──────────────────────────────────────
  obsQueue: {
    borderTopWidth: 1,
    borderTopColor: AdminColors.borderTable,
    overflow: 'hidden',
  },
  obsBand: {},
  obsBandBorder: {
    borderTopWidth: 1,
    borderTopColor: AdminColors.borderTable,
  },
  obsBandHead: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 48,
    paddingHorizontal: 14,
    paddingVertical: 10,
    gap: 8,
  },
  bandCaret: {
    flexShrink: 0,
  },
  bandTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    letterSpacing: 0.4,
    flexShrink: 0,
  },
  bandCount: {
    fontFamily: FontFamily.bold,
    fontSize: 11,
    flexShrink: 0,
  },
  bandSub: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    marginLeft: 4,
    flex: 1,
  },
  obsBandBody: {
    backgroundColor: AdminColors.bgSurface,
    padding: 12,
  },

  // ── Note bar ──────────────────────────────────────────────────────────
  noteBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderLeftWidth: 3,
    borderLeftColor: AdminColors.blue700,
    backgroundColor: AdminColors.blue25,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 9,
  },
  noteText: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.blue900,
    flex: 1,
  },
});
